"use client";

import { useState, useEffect } from "react";
import Head from "next/head";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

// Clean color palette - Only Deep Teal Green and White
const colors = {
  primary: "#395750",      // Deep Teal Green - Company color
  primaryDark: "#2A423C",  // Darker teal
  primaryLight: "#4B6B63", // Lighter teal
  white: "#FFFFFF",        // White
  whiteOff: "#F8F9FA",     // Off-white for subtle contrast
  textDark: "#1A2E29",     // Dark text for contrast
};

type PaymentFrequency = "monthly" | "quarterly" | "semi-annually" | "annually";
type PaymentMode = "advance" | "arrears";

type LoanDetails = {
  amount: number;
  term: number;
  rate: number;
  paymentMode: PaymentMode;
  paymentFrequency: PaymentFrequency;
  monthlyPayment: number;
  totalPayment: number;
  totalInterest: number;
};

type PaymentSchedule = {
  period: number;
  payment: number;
  interest: number;
  principal: number;
  balance: number;
}[];

const LoanCalculatorPage = () => {
  const [loanDetails, setLoanDetails] = useState<LoanDetails>({
    amount: 10000,
    term: 12,
    rate: 28,
    paymentMode: "arrears",
    paymentFrequency: "monthly",
    monthlyPayment: 0,
    totalPayment: 0,
    totalInterest: 0,
  });

  const [schedule, setSchedule] = useState<PaymentSchedule>([]);
  const [showAbout, setShowAbout] = useState(false);
  const [showSchedule, setShowSchedule] = useState(false);

  const calculatePayment = () => {
    let periodsPerYear: number;
    let termInPeriods: number;
    
    switch (loanDetails.paymentFrequency) {
      case "monthly":
        periodsPerYear = 12;
        termInPeriods = loanDetails.term;
        break;
      case "quarterly":
        periodsPerYear = 4;
        termInPeriods = Math.ceil(loanDetails.term / 3);
        break;
      case "semi-annually":
        periodsPerYear = 2;
        termInPeriods = Math.ceil(loanDetails.term / 6);
        break;
      case "annually":
        periodsPerYear = 1;
        termInPeriods = Math.ceil(loanDetails.term / 12);
        break;
    }

    const periodRate = loanDetails.rate / 100 / periodsPerYear;
    
    let payment;
    if (loanDetails.paymentMode === "arrears") {
      const numerator = periodRate * Math.pow(1 + periodRate, termInPeriods);
      const denominator = Math.pow(1 + periodRate, termInPeriods) - 1;
      payment = loanDetails.amount * (numerator / denominator);
    } else {
      const numerator = periodRate * Math.pow(1 + periodRate, termInPeriods - 1);
      const denominator = Math.pow(1 + periodRate, termInPeriods) - 1;
      payment = loanDetails.amount * (numerator / denominator);
    }

    const total = payment * termInPeriods;
    const interest = total - loanDetails.amount;

    const newSchedule: PaymentSchedule = [];
    let balance = loanDetails.amount;

    for (let period = 1; period <= termInPeriods; period++) {
      const interestPayment = balance * periodRate;
      const principalPayment = payment - interestPayment;
      balance -= principalPayment;

      newSchedule.push({
        period,
        payment,
        interest: interestPayment,
        principal: principalPayment,
        balance: Math.round(Math.max(balance, 0)),
      });
    }

    setLoanDetails({
      ...loanDetails,
      monthlyPayment: payment,
      totalPayment: total,
      totalInterest: interest,
    });

    setSchedule(newSchedule);
  };

  useEffect(() => {
    calculatePayment();
  }, [
    loanDetails.amount,
    loanDetails.term,
    loanDetails.rate,
    loanDetails.paymentMode,
    loanDetails.paymentFrequency,
  ]);

  const handleNumericChange =
    (field: keyof LoanDetails) => (e: React.ChangeEvent<HTMLInputElement>) => {
      setLoanDetails((prev) => ({
        ...prev,
        [field]: Number(e.target.value),
      }));
    };

  const handleFrequencyChange = (frequency: PaymentFrequency) => {
    setLoanDetails((prev) => ({
      ...prev,
      paymentFrequency: frequency,
    }));
  };

  const handlePrint = () => {
    const printContent = document.getElementById("printable-content");
    const printWindow = window.open("", "", "width=800,height=900");
    if (printWindow && printContent) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Loan Calculator Report</title>
            <style>
              body { font-family: Arial, sans-serif; margin: 20px; }
              .print-header { text-align: center; margin-bottom: 30px; }
              .print-title { color: ${colors.primary}; font-size: 24px; margin-bottom: 10px; }
              .print-section { margin-bottom: 30px; }
              .print-section-title { color: ${colors.primary}; border-bottom: 2px solid ${colors.primary}; padding-bottom: 5px; margin-bottom: 15px; }
              table { width: 100%; border-collapse: collapse; margin-bottom: 20px; }
              th { background-color: ${colors.primary}10; text-align: left; padding: 8px; }
              td { padding: 8px; border-bottom: 1px solid #ddd; }
              .summary-card { background-color: ${colors.primary}10; border-radius: 8px; padding: 15px; margin-bottom: 20px; }
              .summary-value { font-size: 20px; font-weight: bold; color: ${colors.primary}; }
            </style>
          </head>
          <body>
            <div class="print-header">
              <h1 class="print-title">Loan Calculator Report</h1>
              <p>Generated on ${new Date().toLocaleDateString()}</p>
            </div>
            ${printContent.innerHTML}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      setTimeout(() => {
        printWindow.print();
        printWindow.close();
      }, 500);
    }
  };

  return (
    <div className="min-h-screen pt-28" style={{ background: `linear-gradient(135deg, ${colors.primary}10, ${colors.primary}05)` }}>
      <Head>
        <title>EcoFinance Calculator | Smart Loan Analysis</title>
      </Head>

      {/* Hero Section */}
      <div className="py-16 text-white shadow-xl" style={{ background: `linear-gradient(135deg, ${colors.primary}, ${colors.primaryLight})` }}>
        <div className="container mx-auto px-4 text-center">
          <h1 className="animate-fade-in mb-4 text-4xl font-bold md:text-6xl">
            Smart Loan Calculator
          </h1>
          <p className="text-xl font-light opacity-90 md:text-2xl">
            Visualize Your Financial Journey with Interactive Analytics
          </p>
        </div>
      </div>

      <main className="container mx-auto -mt-12 px-4 py-12">
        <div className="rounded-2xl border shadow-2xl" style={{ borderColor: `${colors.primary}20`, backgroundColor: colors.white }}>
          <div id="printable-content" className="grid gap-8 p-8 lg:grid-cols-3">
            {/* Input Section */}
            <div className="space-y-8 lg:col-span-1">
              <div className="rounded-xl border p-6 shadow-sm" style={{ borderColor: `${colors.primary}20`, background: `linear-gradient(135deg, ${colors.primary}05, ${colors.white})` }}>
                <h2 className="mb-6 flex items-center text-2xl font-semibold" style={{ color: colors.primary }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    className="mr-2 h-6 w-6"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                    />
                  </svg>
                  Loan Parameters
                </h2>

                <div className="space-y-6">
                  <div>
                    <label className="mb-2 block text-sm font-medium" style={{ color: colors.primary }}>
                      Loan Amount (Br)
                    </label>
                    <div className="group relative">
                      <div className="absolute inset-y-0 left-0 flex items-center pl-3" style={{ color: colors.primary }}>
                        <span className="text-lg font-semibold">Br</span>
                      </div>
                      <input
                        type="number"
                        value={loanDetails.amount}
                        onChange={handleNumericChange("amount")}
                        className="w-full rounded-lg border-2 py-3 pl-10 pr-4 transition-all focus:ring-2"
                        style={{ borderColor: `${colors.primary}20`, color: colors.textDark }}
                        min="1000"
                        step="500"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium" style={{ color: colors.primary }}>
                      Loan Term (months)
                    </label>
                    <input
                      type="range"
                      min="6"
                      max="360"
                      value={loanDetails.term}
                      onChange={handleNumericChange("term")}
                      className="range w-full"
                      style={{ accentColor: colors.primary }}
                    />
                    <div className="mt-1 flex justify-between text-sm" style={{ color: colors.primary }}>
                      <span>6</span>
                      <span className="font-medium">{loanDetails.term}</span>
                      <span>360</span>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium" style={{ color: colors.primary }}>
                      Annual Interest Rate (%)
                    </label>
                    <div className="relative">
                      <span className="absolute inset-y-0 right-0 flex items-center pr-3" style={{ color: colors.primary }}>
                        %
                      </span>
                      <input
                        type="number"
                        value={loanDetails.rate}
                        onChange={handleNumericChange("rate")}
                        className="w-full rounded-lg border-2 p-3 pr-10 transition-all focus:ring-2"
                        style={{ borderColor: `${colors.primary}20`, color: colors.textDark }}
                        min="1"
                        max="30"
                        step="0.1"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium" style={{ color: colors.primary }}>
                      Payment Frequency
                    </label>
                    <div className="grid grid-cols-4 gap-2">
                      <button
                        onClick={() => handleFrequencyChange("monthly")}
                        className={`flex items-center justify-center rounded-lg border-2 p-2 text-sm transition-all ${
                          loanDetails.paymentFrequency === "monthly"
                            ? "font-medium"
                            : ""
                        }`}
                        style={{
                          borderColor: loanDetails.paymentFrequency === "monthly" ? colors.primary : `${colors.primary}20`,
                          backgroundColor: loanDetails.paymentFrequency === "monthly" ? `${colors.primary}10` : "transparent",
                          color: loanDetails.paymentFrequency === "monthly" ? colors.primary : colors.textDark
                        }}
                      >
                        Monthly
                      </button>
                      <button
                        onClick={() => handleFrequencyChange("quarterly")}
                        className={`flex items-center justify-center rounded-lg border-2 p-2 text-sm transition-all ${
                          loanDetails.paymentFrequency === "quarterly"
                            ? "font-medium"
                            : ""
                        }`}
                        style={{
                          borderColor: loanDetails.paymentFrequency === "quarterly" ? colors.primary : `${colors.primary}20`,
                          backgroundColor: loanDetails.paymentFrequency === "quarterly" ? `${colors.primary}10` : "transparent",
                          color: loanDetails.paymentFrequency === "quarterly" ? colors.primary : colors.textDark
                        }}
                      >
                        Quarterly
                      </button>
                      <button
                        onClick={() => handleFrequencyChange("semi-annually")}
                        className={`flex items-center justify-center rounded-lg border-2 p-2 text-sm transition-all ${
                          loanDetails.paymentFrequency === "semi-annually"
                            ? "font-medium"
                            : ""
                        }`}
                        style={{
                          borderColor: loanDetails.paymentFrequency === "semi-annually" ? colors.primary : `${colors.primary}20`,
                          backgroundColor: loanDetails.paymentFrequency === "semi-annually" ? `${colors.primary}10` : "transparent",
                          color: loanDetails.paymentFrequency === "semi-annually" ? colors.primary : colors.textDark
                        }}
                      >
                        Semi-Annual
                      </button>
                      <button
                        onClick={() => handleFrequencyChange("annually")}
                        className={`flex items-center justify-center rounded-lg border-2 p-2 text-sm transition-all ${
                          loanDetails.paymentFrequency === "annually"
                            ? "font-medium"
                            : ""
                        }`}
                        style={{
                          borderColor: loanDetails.paymentFrequency === "annually" ? colors.primary : `${colors.primary}20`,
                          backgroundColor: loanDetails.paymentFrequency === "annually" ? `${colors.primary}10` : "transparent",
                          color: loanDetails.paymentFrequency === "annually" ? colors.primary : colors.textDark
                        }}
                      >
                        Annually
                      </button>
                    </div>
                  </div>

                  <div>
                    <label className="mb-2 block text-sm font-medium" style={{ color: colors.primary }}>
                      Payment Mode
                    </label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        onClick={() =>
                          setLoanDetails((prev) => ({
                            ...prev,
                            paymentMode: "arrears",
                          }))
                        }
                        className={`flex items-center justify-center rounded-lg border-2 p-3 transition-all ${
                          loanDetails.paymentMode === "arrears"
                            ? "font-medium"
                            : ""
                        }`}
                        style={{
                          borderColor: loanDetails.paymentMode === "arrears" ? colors.primary : `${colors.primary}20`,
                          backgroundColor: loanDetails.paymentMode === "arrears" ? `${colors.primary}10` : "transparent",
                          color: loanDetails.paymentMode === "arrears" ? colors.primary : colors.textDark
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mr-2 h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                          />
                        </svg>
                        In Arrears
                      </button>
                      <button
                        onClick={() =>
                          setLoanDetails((prev) => ({
                            ...prev,
                            paymentMode: "advance",
                          }))
                        }
                        className={`flex items-center justify-center rounded-lg border-2 p-3 transition-all ${
                          loanDetails.paymentMode === "advance"
                            ? "font-medium"
                            : ""
                        }`}
                        style={{
                          borderColor: loanDetails.paymentMode === "advance" ? colors.primary : `${colors.primary}20`,
                          backgroundColor: loanDetails.paymentMode === "advance" ? `${colors.primary}10` : "transparent",
                          color: loanDetails.paymentMode === "advance" ? colors.primary : colors.textDark
                        }}
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          className="mr-2 h-5 w-5"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
                          />
                        </svg>
                        In Advance
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="rounded-xl border p-6 shadow-sm" style={{ borderColor: `${colors.primary}20`, background: `linear-gradient(135deg, ${colors.primary}05, ${colors.white})` }}>
                <h3 className="mb-4 text-lg font-semibold" style={{ color: colors.primary }}>
                  Payment Distribution
                </h3>
                <div className="relative h-40 w-full">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div
                      className="radial-progress"
                      style={
                        {
                          "--value": (loanDetails.totalInterest / loanDetails.totalPayment) * 100,
                          "--size": "120px",
                          "--thickness": "12px",
                        } as React.CSSProperties
                      }
                    >
                      <div className="text-sm font-semibold" style={{ color: colors.primary }}>
                        {((loanDetails.totalInterest / loanDetails.totalPayment) * 100).toFixed(1)}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Results Section */}
            <div className="space-y-8 lg:col-span-2">
              <div className="grid grid-cols-1 gap-6 md:grid-cols-3">
                <div className="rounded-xl p-6 text-white shadow-lg" style={{ backgroundColor: colors.primary }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-light">
                      {loanDetails.paymentFrequency === "monthly" 
                        ? "Monthly" 
                        : loanDetails.paymentFrequency === "quarterly" 
                          ? "Quarterly" 
                          : loanDetails.paymentFrequency === "semi-annually"
                            ? "Semi-Annual"
                            : "Annual"} Payment
                    </span>
                  </div>
                  <div className="mt-2 text-3xl font-bold">
                    Br {loanDetails.monthlyPayment.toFixed(2)}
                  </div>
                </div>

                <div className="rounded-xl p-6 text-white shadow-lg" style={{ backgroundColor: colors.primary }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-light">Total Interest</span>
                  </div>
                  <div className="mt-2 text-3xl font-bold">
                    Br {loanDetails.totalInterest.toFixed(2)}
                  </div>
                </div>

                <div className="rounded-xl p-6 text-white shadow-lg" style={{ backgroundColor: colors.primary }}>
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-light">Total Payment</span>
                  </div>
                  <div className="mt-2 text-3xl font-bold">
                    Br {loanDetails.totalPayment.toFixed(2)}
                  </div>
                </div>
              </div>

              <div className="rounded-xl border p-6 shadow-sm" style={{ borderColor: `${colors.primary}20`, backgroundColor: colors.white }}>
                <div className="mb-6 flex items-center justify-between">
                  <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
                    Payment Timeline Analysis
                  </h3>
                  <button
                    onClick={handlePrint}
                    className="flex items-center rounded-lg px-4 py-2 transition-colors hover:opacity-80"
                    style={{ backgroundColor: `${colors.primary}10`, color: colors.primary }}
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="mr-2 h-5 w-5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5 4v3H4a2 2 0 00-2 2v3a2 2 0 002 2h1v2a2 2 0 002 2h6a2 2 0 002-2v-2h1a2 2 0 002-2V9a2 2 0 00-2-2h-1V4a2 2 0 00-2-2H7a2 2 0 00-2 2zm8 0H7v3h6V4zm0 8H7v4h6v-4z"
                        clipRule="evenodd"
                      />
                    </svg>
                    Export
                  </button>
                </div>
                <div className="h-96">
                  <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={schedule}>
                      <defs>
                        <linearGradient
                          id="chartGradient"
                          x1="0"
                          y1="0"
                          x2="0"
                          y2="1"
                        >
                          <stop
                            offset="5%"
                            stopColor={colors.primary}
                            stopOpacity={0.2}
                          />
                          <stop
                            offset="95%"
                            stopColor={colors.primary}
                            stopOpacity={0}
                          />
                        </linearGradient>
                      </defs>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        vertical={false}
                        stroke="#e5e7eb"
                      />
                      <XAxis
                        dataKey="period"
                        tick={{ fill: colors.textDark }}
                        axisLine={{ stroke: "#e5e7eb" }}
                      />
                      <YAxis
                        tick={{ fill: colors.textDark }}
                        axisLine={{ stroke: "#e5e7eb" }}
                      />
                      <Tooltip
                        contentStyle={{
                          background: colors.white,
                          border: `1px solid ${colors.primary}20`,
                          borderRadius: "8px",
                          boxShadow: "0 4px 6px -1px rgba(0, 0, 0, 0.1)",
                        }}
                      />
                      <Area
                        type="monotone"
                        dataKey="balance"
                        stroke={colors.primary}
                        strokeWidth={2}
                        fill="url(#chartGradient)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                </div>
              </div>

              <div className="overflow-hidden rounded-xl border shadow-sm" style={{ borderColor: `${colors.primary}20`, backgroundColor: colors.white }}>
                <div className="border-b p-6" style={{ borderColor: `${colors.primary}20`, background: `linear-gradient(135deg, ${colors.primary}05, ${colors.white})` }}>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-semibold" style={{ color: colors.primary }}>
                      Payment Schedule
                    </h3>
                    <button
                      className="flex items-center rounded-lg px-4 py-2 transition-colors hover:opacity-80"
                      style={{ color: colors.primary }}
                      onClick={() => setShowSchedule(!showSchedule)}
                      aria-expanded={showSchedule}
                    >
                      <span>Show Details</span>
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className={`ml-2 h-5 w-5 transform transition-transform ${showSchedule ? "rotate-180" : ""}`}
                        viewBox="0 0 20 20"
                        fill="currentColor"
                      >
                        <path
                          fillRule="evenodd"
                          d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                          clipRule="evenodd"
                        />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="overflow-x-auto">
                  {showSchedule && (
                    <table className="w-full">
                      <thead style={{ backgroundColor: `${colors.primary}10` }}>
                        <tr>
                          {[
                            loanDetails.paymentFrequency === "monthly" 
                              ? "Month" 
                              : loanDetails.paymentFrequency === "quarterly" 
                                ? "Quarter" 
                                : loanDetails.paymentFrequency === "semi-annually"
                                  ? "Half-Year"
                                  : "Year",
                            "Principal",
                            "Interest",
                            "Balance"
                          ].map((header) => (
                            <th
                              key={header}
                              className="px-2 md:px-6 py-3 text-left text-sm font-semibold"
                              style={{ color: colors.primary }}
                            >
                              {header}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody className="divide-y" style={{ borderColor: `${colors.primary}20` }}>
                        {schedule.map((entry) => (
                          <tr
                            key={entry.period}
                            className="transition-colors hover:opacity-80"
                            style={{ backgroundColor: colors.white }}
                          >
                            <td className="px-2 md:px-6 py-4 text-sm font-medium" style={{ color: colors.primary }}>
                              {entry.period}
                            </td>
                            <td className="px-2 md:px-6 py-4 text-sm" style={{ color: colors.textDark }}>
                              Br{entry.principal.toFixed(2)}
                            </td>
                            <td className="px-2 md:px-6 py-4 text-sm" style={{ color: "#DC2626" }}>
                              Br{entry.interest.toFixed(2)}
                            </td>
                            <td className="px-2 md:px-6 py-4 text-sm" style={{ color: colors.textDark }}>
                              Br{entry.balance.toFixed(2)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default LoanCalculatorPage;